<script setup>
import { computed } from 'vue';
import DownCaretIcon from '@/js/core/components/icons/DownCaretIcon.vue';
import TrayUserSelector from '@/js/core/components/parts/trays/TrayUserSelector.vue';
import {
  COMMENTS_PERMISSION_OPTIONS,
  VISIBILITY_OPTIONS,
} from '@/js/core/constants/tray-constants';

const props = defineProps({
  visibleTo: {
    type: String,
    required: true,
  },
  commentAccess: {
    type: String,
    required: true,
  },
  visibleUsers: {
    type: Array,
    default: () => [],
  },
  commentableUsers: {
    type: Array,
    default: () => [],
  },
  // Owner-side managers, who hold access and comment rights by role rather than by selection.
  // Shown alongside the selected users so the roster reads complete.
  roleUsers: {
    type: Array,
    default: () => [],
  },
  errors: {
    type: Object,
    default: () => ({}),
  },
  idPrefix: {
    type: String,
    default: 'tray',
  },
  showRequired: {
    type: Boolean,
    default: false,
  },
});

const membersOnlyWithoutMembers = computed(
  () => props.visibleTo === 'members' && props.visibleUsers.length === 0
);

const specificCommentsWithoutCommenters = computed(
  () => props.commentAccess === 'specific' && props.commentableUsers.length === 0
);

defineEmits([
  'update:visibleTo',
  'update:commentAccess',
  'update:visibleUsers',
  'update:commentableUsers',
]);
</script>

<template>
  <div class="tray-form__group tray-access-group">
    <label :for="`${idPrefix}-visible-to`" class="tray-form__label">
      Visible to<span v-if="showRequired" class="tray-form__required">*</span>
    </label>
    <div class="tray-form__select-wrapper">
      <select
        :id="`${idPrefix}-visible-to`"
        :value="visibleTo"
        class="tray-form__select"
        :required="showRequired"
        :aria-invalid="errors.visibility ? 'true' : undefined"
        :aria-describedby="errors.visibility ? `${idPrefix}-visible-to-error` : undefined"
        @change="$emit('update:visibleTo', $event.target.value)"
      >
        <option v-for="option in VISIBILITY_OPTIONS" :key="option.value" :value="option.value">
          {{ option.label }}
        </option>
      </select>
      <DownCaretIcon class="tray-form__select-icon" />
    </div>
    <p
      v-if="errors.visibility"
      :id="`${idPrefix}-visible-to-error`"
      class="tray-form__field-error"
      role="alert"
    >
      {{ errors.visibility }}
    </p>
    <template v-if="visibleTo === 'members'">
      <TrayUserSelector
        :model-value="visibleUsers"
        :input-id="`${idPrefix}-visible-users`"
        search-label="Add someone who can access this Tray"
        :role-users="roleUsers"
        :error="errors.visible_user_uuids"
        @update:model-value="$emit('update:visibleUsers', $event)"
      />
      <p v-if="membersOnlyWithoutMembers" class="tray-form__hint">
        Only you and this Tray's managers can see it until you add members.
      </p>
    </template>
  </div>

  <slot name="after-visibility" />

  <div class="tray-form__group tray-access-group">
    <label :for="`${idPrefix}-comments-by`" class="tray-form__label">
      Comments can be added by<span v-if="showRequired" class="tray-form__required">*</span>
    </label>
    <div class="tray-form__select-wrapper">
      <select
        :id="`${idPrefix}-comments-by`"
        :value="commentAccess"
        class="tray-form__select"
        :required="showRequired"
        :aria-invalid="errors.comment_access ? 'true' : undefined"
        :aria-describedby="errors.comment_access ? `${idPrefix}-comments-by-error` : undefined"
        @change="$emit('update:commentAccess', $event.target.value)"
      >
        <option
          v-for="option in COMMENTS_PERMISSION_OPTIONS"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
      <DownCaretIcon class="tray-form__select-icon" />
    </div>
    <p
      v-if="errors.comment_access"
      :id="`${idPrefix}-comments-by-error`"
      class="tray-form__field-error"
      role="alert"
    >
      {{ errors.comment_access }}
    </p>
    <template v-if="commentAccess === 'specific'">
      <TrayUserSelector
        :model-value="commentableUsers"
        :input-id="`${idPrefix}-commentable-users`"
        search-label="Add someone who can comment on this Tray"
        :role-users="roleUsers"
        :error="errors.commentable_user_uuids"
        @update:model-value="$emit('update:commentableUsers', $event)"
      />
      <p v-if="specificCommentsWithoutCommenters" class="tray-form__hint">
        Only you and this Tray's managers can comment until you add people.
      </p>
    </template>
  </div>
</template>
